/* eslint-disable camelcase */
import axios from 'axios';
import { useFormik } from 'formik';
import { get } from 'lodash-es';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import SlidingPane from 'react-sliding-pane';
import * as Yup from 'yup';
import { setOffer } from '../../../../../actions/offer.actions';
import Button from '../../../../../shared/components/Button';
import ObjectAttribute, { UIElementType, generateOASchema } from '../../../../../shared/components/ObjectAttribute/ObjectAttribute';
import useBreakpoint, { SIZE_XS } from '../../../../../shared/hooks/useBreakpoint';
import { ACTIONS } from '../../../../../utils/constants';

const OBJECT_ATTRIBUTE_TYPE_UPDATE = {
  'general': {
    body: 'user_offer'
  },
  'user_post': {
    body: 'user_post'
  },
}

/**
 *
 * @typedef ObjectAttributesSidePaneProps
 * @prop {boolean} open
 * @prop {function} onRequestClose
 * @prop {import('../../../../../utils/types/types').ObjectAttributeMetadata} objectAttributeMetadata
 * @prop {Object<string, any>} objectAttributeValues
 * @prop {string} objectAttributeEndpoint
 * @prop {'general'|'user_post'} updateType
 *
 * @param {ObjectAttributesSidePaneProps} props
 * @returns
 */
const ObjectAttributesSidePane = ({
  open,
  onRequestClose,
  onAfterClose,
  objectAttributeMetadata,
  objectAttributeValues,
  objectAttributeEndpoint,
  updateType = 'general'
}) => {
  const breakpoint = useBreakpoint();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [state, setState] = useState({
    loading: false,
    hasBeenSubmitted: false,
    success: undefined,
    action: null,
    error: null
  });
  /** @type {import('../../../../../reducers/offer.reducer').Offer} */
  const offer = useSelector(store => get(store, 'offer.offer'));

  const editableAttrsValues = useCallback(() => {
    const initialValues = {};
    if (!objectAttributeMetadata || !objectAttributeMetadata.length) return initialValues;

    objectAttributeMetadata.forEach((attr) => {
      const value = get(objectAttributeValues, `${attr.code}`);
      if (
        [UIElementType.CHECKBOXES, UIElementType.DROPDOWN].includes(attr.ui_element)
        && attr.is_multiple
      ) {
        if (Array.isArray(value)) {
          initialValues[attr.code] = value;
        } else {
          initialValues[attr.code] = (value && value.split(',')) || '';
        }
      } else {
        if (Array.isArray(value)) {
          initialValues[attr.code] = value[0] || '';
        } else {
          initialValues[attr.code] = value || '';
        }
      }
    });

    return initialValues;
  }, [objectAttributeMetadata, objectAttributeValues]);

  const formik = useFormik({
    initialValues: {
      ...editableAttrsValues()
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      ...generateOASchema(objectAttributeMetadata, t)
    }),
    onSubmit: values => {
      setState({
        ...state,
        loading: true
      });

      const oas = {};
      objectAttributeMetadata.forEach(objectAttribute => {
        oas[objectAttribute.code] = values[objectAttribute.code];
      });

      const payload = {
        ...oas,
        skip_validation: true,
      };

      axios.patch(objectAttributeEndpoint, {
        [OBJECT_ATTRIBUTE_TYPE_UPDATE[updateType].body]: payload
      }).then(({ data }) => {
        if (data.user_offer) {
          const userOfferTmp = { ...data.user_offer };
          const offerTmp = {
            ...offer,
            user_offer: userOfferTmp
          };
          dispatch(setOffer(offerTmp));
        }
        setState({
          ...state,
          loading: false,
          success: true,
          action: ACTIONS.UPDATE
        });
        onRequestClose();
      }).catch(err => {
        setState({
          ...state,
          error: err,
          loading: false,
          success: false,
        });
        onRequestClose();
      });
    },
  });

  const renderObjectAttributes = useCallback(() => {
    if (!objectAttributeMetadata || !objectAttributeMetadata.length) return null;

    return (<React.Fragment>
      <h4 className='mb-4 text-capitalize'>
        {offer.title} {t('influencers.common.questions')}
      </h4>
      {
        objectAttributeMetadata.map(objectAttribute => {
          const code = objectAttribute.code;
          const isTouched = formik.touched && formik.touched[code];
          const hasError = formik.errors && formik.errors[code];

          return (<Form.Group key={code}>
            <ObjectAttribute
              fieldData={objectAttribute}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values[code]}
              name={code}
              error={!!(isTouched && hasError)} />
            {isTouched && hasError ?
              <Form.Text className="text-muted danger-text">
                {hasError}
              </Form.Text> : null}
          </Form.Group>);
        })
      }
    </React.Fragment>);
  }, [formik, objectAttributeMetadata]);

  return (<SlidingPane
    overlayClassName="slide-over"
    isOpen={open}
    closeIcon={null}
    width={breakpoint === SIZE_XS ? "100%" : "450px"}
    shouldCloseOnEsc
    onRequestClose={onRequestClose}
    onAfterClose={() => onAfterClose(state.success, state.action, state.error)}
    hideHeader
  >
    <div>
      <Form onSubmit={formik.handleSubmit}>
        {formik.values
          ? renderObjectAttributes()
          : null
        }

        <hr className='my-6' />
        <div className='d-flex justify-content-end'>
          <Button
            variant="tertiary"
            type="button"
            onClick={() => onRequestClose()}
            disabled={state.loading}>
            {t('influencers.common.cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={
              state.loading || !formik.dirty || !formik.isValid
            }>
            {state.loading
              ? <Spinner animation="border" size="sm" />
              : t('influencers.common.save')
            }
          </Button>
        </div>
      </Form>
    </div>
  </SlidingPane>);
};


ObjectAttributesSidePane.propTypes = {
  open: PropTypes.bool,
  onRequestClose: PropTypes.func.isRequired,
  onAfterClose: PropTypes.func,
  objectAttributeMetadata: PropTypes.arrayOf(PropTypes.object).isRequired,
  objectAttributeValues: PropTypes.object,
  objectAttributeEndpoint: PropTypes.string,
  updateType: PropTypes.oneOf(['general', 'user_post'])
};

ObjectAttributesSidePane.defaultProps = {
  open: false,
  onAfterClose: () => { },
  objectAttributeValues: {},
};

export default ObjectAttributesSidePane;